import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';

import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';

import { toast } from 'sonner';
import { cn } from '../../lib/utils';
import { useDocumentManager } from '../../hooks/useDocumentManager';
import { DownloadProgress, DownloadProgressBar } from '../ui/download-progress';
import GEDDocumentViewerModal from './GEDDocumentViewerModal';
import { Folder, FileText, Download, Search, Filter, RefreshCw, Eye, Building2, Shield, Users, FileSignature, Home, ChevronDown, ChevronRight, FolderOpen, HardDrive, User, Tag, Hash, Clock, CheckCircle2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

import { SOURCE_COLORS, SOURCE_ICONS, SourceIcon, CollapsibleDocRow } from './GEDDocRow';

export default function GEDUniverselle() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('tous');
  const [documents, setDocuments] = useState([]);
  const [statistics, setStatistics] = useState(null);
  const [sources, setSources] = useState([]);
  const [selectedSource, setSelectedSource] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedIds, setExpandedIds] = useState(new Set());
  const [showAllBySource, setShowAllBySource] = useState(new Set());
  const [previewDoc, setPreviewDoc] = useState(null);

  useEffect(() => {
    if (token) {
      fetchSources();
      fetchStatistics();
      fetchDocuments();
    }
  }, [token]);

  useEffect(() => {
    if (token) fetchDocuments();
  }, [selectedSource]);

  const fetchSources = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ged/sources`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setSources(data.sources || []);
      }
    } catch {
    }
  };

  const fetchStatistics = async () => {
    try {
      const response = await fetch(`${API_URL}/api/ged/statistics`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setStatistics(data);
      }
    } catch {
    }
  };

  const fetchDocuments = async () => {
    setLoading(true);
    try {
      // Use GED Universal endpoints for proper source filtering
      let url;
      if (selectedSource) {
        // Use source-specific endpoint for filtered documents
        url = `${API_URL}/api/ged/documents/source/${selectedSource}?limit=200`;
        const response = await fetch(url, { headers: { 'Authorization': `Bearer ${token}` } });
        if (response.ok) {
          const data = await response.json();
          setDocuments(Array.isArray(data) ? data : (data.documents || data.data || []));
        }
      } else {
        // For all documents, fetch from all sources and combine
        const allDocs = [];
        const sourcesToFetch = ['BATICO', 'MEMBRE', 'FTPE', 'DOMIUM', 'ARCHOS', 'NOMINATIVE', 'AVENANT', 'AUTRE'];
        for (const source of sourcesToFetch) {
          try {
            const response = await fetch(
              `${API_URL}/api/ged/documents/source/${source}?limit=100`,
              { headers: { 'Authorization': `Bearer ${token}` } }
            );
            if (response.ok) {
              const data = await response.json();
              const docs = Array.isArray(data) ? data : (data.documents || []);
              allDocs.push(...docs);
            }
          } catch {
            // Continue with other sources
          }
        }
        setDocuments(allDocs);
      }
    } catch {
      toast.error('Erreur chargement des documents');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) { fetchDocuments(); return; }
    setLoading(true);
    try {
      // Use GED Universal search endpoint
      const response = await fetch(
        `${API_URL}/api/ged/search?q=${encodeURIComponent(searchQuery)}&limit=200`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      if (response.ok) {
        const data = await response.json();
        const docs = Array.isArray(data) ? data : (data.documents || data.data || []);
        setDocuments(docs);
        toast.success(`${docs.length} document(s) trouve(s)`);
      }
    } catch {
      toast.error('Erreur recherche');
    } finally {
      setLoading(false);
    }
  };

  // Hook professionnel de gestion des documents
  const { downloads, downloadDocument: hookDownload, previewDocument } = useDocumentManager(token);

  const downloadDocument = (docId, filename) => {
    hookDownload(docId, filename);
  };

  const viewDocument = (doc) => {
    // Ouverture in-app via DocumentPreviewModal (iframe + zoom + print + download)
    setPreviewDoc(doc);
  };

  const toggleExpand = useCallback((id) => {
    setExpandedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }, []);

  const toggleShowAll = useCallback((source) => {
    setShowAllBySource(prev => {
      const next = new Set(prev);
      if (next.has(source)) next.delete(source); else next.add(source);
      return next;
    });
  }, []);

  const getFilteredDocuments = () => {
    if (activeTab === 'tous') return documents;
    return documents.filter(doc => (doc.source || doc.categorie) === activeTab);
  };

  const getDocumentsBySource = () => {
    const grouped = {};
    documents.forEach(doc => {
      const source = doc.source || doc.categorie || 'AUTRE';
      if (!grouped[source]) grouped[source] = [];
      grouped[source].push(doc);
    });
    return grouped;
  };

  return (
    <div className="space-y-4" data-testid="ged-universelle-container">
      {/* Header compact */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
            <FolderOpen className="h-5 w-5 text-[#002d5b]" />
            GED Universelle
          </h2>
          <p className="text-sm text-gray-500">Gestion Electronique des Documents - Tous les workflows</p>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={() => { fetchDocuments(); fetchStatistics(); }}
          disabled={loading}
          className="gap-1.5"
        >
          <RefreshCw className={cn("h-3.5 w-3.5", loading && "animate-spin")} />
          Actualiser
        </Button>
      </div>

      {/* Stats row */}
      {statistics && (
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-2">
          <button
            onClick={() => { setSelectedSource(''); setActiveTab('tous'); }}
            className={cn(
              "flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
              activeTab === 'tous' ? "border-blue-300 shadow-sm bg-blue-50/50" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
            )}
          >
            <HardDrive className="h-4 w-4 text-[#002d5b] shrink-0" />
            <div>
              <p className="text-[10px] text-gray-400 uppercase tracking-wider">Total</p>
              <p className="text-lg font-bold text-gray-900">{statistics.total_documents}</p>
              <p className="text-[10px] text-gray-400">{statistics.total_size_mb} MB</p>
            </div>
          </button>
          {Object.entries(statistics.by_source || {}).map(([source, data]) => {
            const colors = SOURCE_COLORS[source] || SOURCE_COLORS.AUTRE;
            const Icon = SOURCE_ICONS[source] || Folder;
            return (
              <button
                key={source}
                onClick={() => { setSelectedSource(source); setActiveTab(source); }}
                className={cn(
                  "flex items-center gap-3 p-3 rounded-lg border transition-all text-left",
                  activeTab === source ? "border-blue-300 shadow-sm bg-blue-50/50" : "border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm"
                )}
              >
                <Icon className={cn("h-4 w-4 shrink-0", colors.text)} />
                <div>
                  <p className="text-[10px] text-gray-400 uppercase tracking-wider">{source}</p>
                  <p className={cn("text-lg font-bold", colors.text)}>{data.count}</p>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Search + source filter */}
      <div className="flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input
            placeholder="Rechercher par reference, description, email..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            className="pl-10"
            data-testid="ged-search-input"
          />
        </div>
        <Button onClick={handleSearch} disabled={loading} size="sm" className="gap-1.5">
          <Search className="h-3.5 w-3.5" /> Rechercher
        </Button>
        <Select value={selectedSource} onValueChange={(v) => { setSelectedSource(v === 'all' ? '' : v); if (v !== 'all') setActiveTab(v); else setActiveTab('tous'); }}>
          <SelectTrigger className="w-48" data-testid="ged-source-filter">
            <Filter className="h-4 w-4 mr-2" />
            <SelectValue placeholder="Toutes les sources" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Toutes les sources</SelectItem>
            {sources.map(source => (
              <SelectItem key={source.code} value={source.code}>{source.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Count */}
      <div className="flex items-center gap-3 px-1">
        <span className="text-sm text-gray-500">{getFilteredDocuments().length} document(s)</span>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="flex flex-wrap h-auto gap-1 p-1">
          <TabsTrigger value="tous" className="px-4 gap-1.5">
            <Folder className="h-3.5 w-3.5" />
            Tous ({documents.length})
          </TabsTrigger>
          {sources.map(source => {
            const count = documents.filter(d => d.source === source.code).length;
            const colors = SOURCE_COLORS[source.code] || SOURCE_COLORS.AUTRE;
            return (
              <TabsTrigger
                key={source.code}
                value={source.code}
                className={cn("px-3 gap-1.5", activeTab === source.code && colors.bg)}
              >
                <SourceIcon source={source.code} />
                <span>{source.code}</span>
                {count > 0 && <Badge variant="secondary" className="text-[10px] ml-1">{count}</Badge>}
              </TabsTrigger>
            );
          })}
        </TabsList>

        {/* Tab: Tous */}
        <TabsContent value="tous" className="mt-4">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-12 h-12 border-3 border-blue-500 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : documents.length === 0 ? (
            <div className="bg-white rounded-xl border p-12 text-center">
              <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-700 font-medium">Aucun document trouve</p>
              <p className="text-sm text-gray-400">Les documents generes par les differents workflows apparaitront ici</p>
            </div>
          ) : (
            <div className="space-y-6">
              {Object.entries(getDocumentsBySource()).map(([source, docs]) => {
                const colors = SOURCE_COLORS[source] || SOURCE_COLORS.AUTRE;
                const sourceInfo = sources.find(s => s.code === source);
                const showAll = showAllBySource.has(source);
                const displayDocs = showAll ? docs : docs.slice(0, 5);
                return (
                  <div key={source} className="space-y-2">
                    <div className={cn("flex items-center gap-2 px-1")}>
                      <SourceIcon source={source} />
                      <span className={cn("text-sm font-semibold", colors.text)}>{sourceInfo?.label || source}</span>
                      <Badge className={cn("text-[10px]", colors.bg, colors.text)}>{docs.length}</Badge>
                    </div>
                    <div className="space-y-1" data-testid={`ged-source-group-${source}`}>
                      {displayDocs.map(doc => (
                        <CollapsibleDocRow
                          key={doc.ged_id || doc.id}
                          doc={doc}
                          isExpanded={expandedIds.has(doc.ged_id || doc.id)}
                          onToggle={toggleExpand}
                          onDownload={downloadDocument}
                          onView={viewDocument}
                          downloadStatus={downloads}
                        />
                      ))}
                    </div>
                    {docs.length > 5 && !showAll && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleShowAll(source)}
                        className="text-xs gap-1.5 ml-1"
                      >
                        Voir les {docs.length - 5} autres documents
                        <ChevronRight className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {showAll && docs.length > 5 && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleShowAll(source)}
                        className="text-xs gap-1.5 ml-1"
                      >
                        Reduire
                        <ChevronDown className="h-3.5 w-3.5 rotate-180" />
                      </Button>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Tabs: Per source */}
        {sources.map(source => (
          <TabsContent key={source.code} value={source.code} className="mt-4">
            {getFilteredDocuments().length === 0 ? (
              <div className="bg-white rounded-xl border p-12 text-center">
                <FileText className="w-12 h-12 mx-auto text-gray-300 mb-3" />
                <p className="text-gray-700 font-medium">Aucun document {source.label}</p>
                <p className="text-sm text-gray-400">Les documents generes apparaitront ici</p>
              </div>
            ) : (
              <div className="space-y-1" data-testid={`ged-source-list-${source.code}`}>
                {getFilteredDocuments().map(doc => (
                  <CollapsibleDocRow
                    key={doc.ged_id || doc.id}
                    doc={doc}
                    isExpanded={expandedIds.has(doc.ged_id || doc.id)}
                    onToggle={toggleExpand}
                    onDownload={downloadDocument}
                    onView={viewDocument}
                    showSource={false}
                    downloadStatus={downloads}
                  />
                ))}
              </div>
            )}
          </TabsContent>
        ))}
      </Tabs>

      {/* Modal de prévisualisation in-app pro (iframe + zoom + print + download) */}
      {previewDoc && (
        <GEDDocumentViewerModal
          doc={previewDoc}
          onClose={() => setPreviewDoc(null)}
          onDownload={(d) => downloadDocument(d.id, d.original_filename || d.filename)}
          previewDocument={previewDocument}
        />
      )}
    </div>
  );
}
